import React, { useEffect, useState } from 'react';
import { Box, Spinner, Alert, AlertIcon, Flex, Heading } from '@chakra-ui/react';
import { useAuth } from '../../features/auth/hooks/useAuth';
import MesCandidaturesList from './MesCandidaturesList';
import { Candidature, getMesCandidatures } from './CandidatureService';
import { Mission, getMissionById } from '../mission/MissionService';

const MesCandidaturesPage: React.FC = () => {
    const [candidatures, setCandidatures] = useState<(Candidature & { mission?: Mission })[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { userInfo } = useAuth();
    const accentColor = "#420073";

    useEffect(() => {
        if (!userInfo.sub) return;

        setLoading(true);
        let isMounted = true;

        const fetchCandidatures = async () => {
            try {
                const data = await getMesCandidatures(userInfo.sub);

                // Chargement parallèle des missions
                const withMissions = await Promise.all(
                    data.map(async (cand) => {
                        try {
                            const mission = await getMissionById(cand.missionId);
                            return { ...cand, mission };
                        } catch (err) {
                            return cand;
                        }
                    })
                );

                if (isMounted) {
                    setCandidatures(withMissions);
                    setError(null);
                }
            } catch (err) {
                if (isMounted) {
                    console.log("Erreur lors de la récupération des candidatures : ", (err as Error).message);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchCandidatures();

        return () => {
            isMounted = false;
        };
    }, [userInfo.sub]);

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" color={accentColor} />
            </Flex>
        );
    }

    return (
        <Box p={4} maxW="container.xl" mx="auto">
            <Heading mb={8} color={accentColor}>Mes Candidatures</Heading>

            {error && (
                <Alert status="error" mb={4}>
                    <AlertIcon />
                    {error}
                </Alert>
            )}

            {candidatures.length === 0 ? (
                <Box textAlign="center" py={10}>
                    <Heading size="md" color="gray.500">
                        Vous n'avez pas encore postulé à des missions
                    </Heading>
                </Box>
            ) : (
                <MesCandidaturesList candidatures={candidatures} />
            )}
        </Box>
    );
};

export default MesCandidaturesPage;